import React, { useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from 'app/context/ToastContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilterMatchMode, SortOrder } from 'primereact/api';
import { postEntities } from 'app/api/generalApi';
import { Button } from 'primereact/button';
import ImportDataWidget from 'app/shared/protocol/import-data-widget';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { formatFromLocalDate } from 'app/shared/util/date-utils';
import { deleteImportedPayment } from 'app/api/paymentApi';
import { getLightCompaniesWithIban } from 'app/api/insuranceCompanyApi';
import { Dropdown } from 'primereact/dropdown';
import { decode, encode } from 'app/shared/util/encode-utils';
import axios from 'axios';
import { downloadExcelFile } from 'app/api/apiUtil';
import DateTextInput from 'app/component/date-text-input';

const PaymentTable: React.FC = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const initData = searchParams.get('p') ? (decode(searchParams.get('p'), {}) as any) : null;

  const companyId = initData?.companyId ? Number(initData.companyId) : undefined;

  const { showSuccess, showError } = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState<number>();
  const [entity, setEntity] = useState(null);
  const [deleteContactDialog, setDeleteContactDialog] = useState(false);
  const toast = useRef(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [filterValue] = useState<string | null>(null);
  const [lazyParams, setLazyParams] = useState({
    first: initData?.page ? initData?.page * 20 : 0,
    rows: 20,
    page: initData?.page ?? 0,
    sortField: initData?.sort ?? 'id',
    sortOrder: initData?.dirertion ? (initData?.direction === 2 ? SortOrder.DESC : SortOrder.ASC) : SortOrder.DESC,
    filter: '',
    filters: null,
  });
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [filterValues, setFilterValues] = useState(
    initData?.filters || {
      processed: { value: null, matchMode: FilterMatchMode.EQUALS },
      id: { value: null, matchMode: FilterMatchMode.EQUALS },
      ref: { value: null, matchMode: FilterMatchMode.CONTAINS },
      isOutgoing: { value: null, matchMode: FilterMatchMode.EQUALS },
      date: { value: null, matchMode: FilterMatchMode.CONTAINS },
      accountName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      variableSymbol: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  );

  useEffect(() => {
    if (companyId) {
      setSelectedCompany(companyId);
    }
  }, [companyId, insuranceCompanies]);

  useEffect(() => {
    getLightCompaniesWithIban()
      .then(r => {
        setInsuranceCompanies(
          r.data?.map(company => ({
            label: company.name,
            value: company.id,
          })),
        );
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadData();
    }
  }, [lazyParams, selectedCompany]);

  const loadData = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      companyId: selectedCompany,
      ...(lazyParams.filter ? { query: lazyParams.filter } : {}),
    };

    postEntities('/api/payments/search', requestData, filterValues)
      .then(response => {
        setPayments(response.data.content);
        setTotalRecords(response.data.totalElements);
      })
      .catch(() => {
        showError('Chyba', 'Nastala chyba pri načítaní platieb.');
      })
      .finally(() => {
        setLoading(false);
        setIsInitialized(true);
      });
  };

  const onPage = event => {
    setLazyParams({ ...event, filter: filterValue });
  };

  const onSort = event => {
    setLazyParams({ ...event, page: lazyParams.page, filter: filterValue });
  };

  useEffect(() => {
    if (filterValue || filterValues === '' || filterValues) {
      const delayDebounceFn = setTimeout(() => {
        if (isInitialized) {
          setLazyParams({
            ...lazyParams,
            first: 0,
            page: 0,
            filter: filterValue,
          });
        } else {
          setLazyParams({
            ...lazyParams,
            filter: filterValue,
          });
        }
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [filterValue, filterValues]);

  const loadOtherExportPaymentLogs = taskId => {
    axios
      .get(`/api/payments/export/status/${taskId}`)
      .then(response => {
        if (!response.data) {
          setTimeout(() => {
            loadOtherExportPaymentLogs(taskId);
          }, 2000);
        } else {
          axios
            .get(`/api/payments/export/download/${taskId}`, {
              responseType: 'blob', // Important for downloading files
            })
            .then(r => {
              downloadExcelFile(r);
            })
            .catch(e => {
              console.log(e);
              showError('Chyba', 'Neočakávaná chyba pri exporte. Skúste ešte raz.');
            })
            .finally(() => setLoading(false));
        }
      })
      .catch(e => {
        console.error(e);
      });
  };

  const handleExport = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      companyId: selectedCompany,
      ...(lazyParams.filter ? { query: lazyParams.filter } : {}),
    };

    postEntities('/api/payments/export', requestData, filterValues)
      .then(response => {
        setCurrentTaskId(response.data);

        setTimeout(() => {
          loadOtherExportPaymentLogs(response.data);
        }, 1000);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  function getTempParameters() {
    const p = {
      companyId: selectedCompany,
      page: lazyParams.page,
      sort: lazyParams.sortField,
      direction: lazyParams.sortOrder === SortOrder.ASC ? 1 : 2,
      filters: filterValues,
      filter: filterValue,
    };
    return encode(p);
  }

  // Handle the editing of an existing agent
  const handleEdit = rowData => {
    navigate(`edit-imported-payment/${rowData.id}?p=${getTempParameters()}`); // Navigate to the edit page
  };

  const handleDelete = () => {
    setLoading(true);
    deleteImportedPayment(entity.id)
      .then(r => {
        setPayments(prev => prev.filter(p => r.data !== p.id));
        setDeleteContactDialog(false);
        showSuccess('Úspech', 'Platba bola úspešne vymazaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const confirmDeleteContact = contactToDelete => {
    // setProduct(product);
    setEntity(contactToDelete);
    setDeleteContactDialog(true);
  };

  const hideDeleteContactDialog = () => {
    setDeleteContactDialog(false);
  };

  const processedFilterMenu = options => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Všetko', value: null },
          { label: 'Spracované', value: true },
          { label: 'Nespracované', value: false },
        ]}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Vyber"
        className="p-column-filter"
        showClear
      />
    );
  };

  const isOutgoingFilterMenu = options => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Všetko', value: null },
          { label: 'Odchádzajúca', value: true },
          { label: 'Prichádzajúca', value: false },
        ]}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Vyber"
        className="p-column-filter"
        showClear
      />
    );
  };

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          {<Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => handleEdit(rowData)} />}
          {<Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteContact(rowData)} />}
        </div>
      </React.Fragment>
    );
  };

  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button style={{ backgroundColor: 'gray' }} label="Nie" icon="pi pi-times" outlined onClick={hideDeleteContactDialog} />
      <Button style={{ backgroundColor: '#e31818' }} label="Áno" icon="pi pi-check" severity="danger" onClick={handleDelete} />
    </React.Fragment>
  );

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between">
        {/*<Button type="button" icon="pi pi-plus" label="Vytvoriť platbu" className="p-button-success" onClick={handleCreate} />*/}
        <Button style={{ backgroundColor: 'gray' }} type="button" icon="pi pi-download" label="Exportovať data" onClick={handleExport} />
        <ImportDataWidget
          importLabel="Importovať platby"
          importApi={`/api/payments/import/${selectedCompany}`}
          importFileName="platby-protokoly.txt"
          onFinishProcessing={loadData}
          acceptFormat=".csv,.xml,.xlsx"
        />
      </div>
    );
  };

  const header = renderHeader();

  const expandTemplate = rowData => {
    return (
      <div className="p-ml-4">
        <h5 style={{ color: 'blue' }}>Spracovanie platby</h5>
        <DataTable stripedRows value={rowData.contractPayments} responsiveLayout="scroll" size="small">
          <Column field="zaplateneNacas" header="Zaplatené načas" body={v => isProcessedIcon(v.zaplateneNacas)} />
          <Column field="amount" header="Suma" />
          <Column field="fee" header="Poplatok" />
          <Column field="periodFrom" header="Od" body={v => formatFromLocalDate(v.periodFrom)} />
          <Column field="periodTo" header="Do" body={v => formatFromLocalDate(v.periodTo)} />
          <Column field="contract.id" header="Id zmluvy" />
        </DataTable>
        <Divider />
      </div>
    );
  };

  const allowExpansion = rowData => {
    return rowData.contractPayments.length > 0;
  };

  const isProcessedIcon = processed => {
    return (
      <span>
        {processed ? (
          <i className="pi pi-check" style={{ color: 'green', fontWeight: 'bold' }}></i>
        ) : (
          <i className="pi pi-times" style={{ color: 'red', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const paymentType = isOutgoing => {
    return (
      <span>
        {isOutgoing ? (
          <i className="pi pi-arrow-up" style={{ color: 'red', fontWeight: 'bold' }}></i>
        ) : (
          <i className="pi pi-arrow-down" style={{ color: 'green', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const getMessagesIcons = data => {
    return (
      <span className="d-flex">
        {data.errors && <i className="pi pi-exclamation-triangle" title={data.errors} style={{ color: 'red', fontWeight: 'bold' }}></i>}

        {data.warnings && (
          <i className="pi pi-exclamation-triangle" title={data.warnings} style={{ color: 'orange', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const getPaymentExceedLimit = data => {
    if (data?.paymentsExceedLimit) {
      return (
        <span className="d-flex">
          <i
            className="pi pi-euro"
            title="Súčet platieb je vyšší ako sa reálne v tejto transakcii dostalo."
            style={{ color: 'red', fontSize: '20px', fontWeight: 'bold' }}
          ></i>
        </span>
      );
    }

    return '';
  };

  const toLocalYMD = (d?: Date | null) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`; // local yyyy-MM-dd (no timezone)
  };

  const dateFilterTemplate = (options: any) => {
    return <DateTextInput value={options.value} onChange={val => options.filterApplyCallback(val || null)} />;
  };

  return (
    <div>
      <div className="card">
        <div className="p-field">
          <label htmlFor="company">Spoločnosť:</label>
          <Dropdown
            id="company"
            name="company"
            value={selectedCompany}
            options={insuranceCompanies}
            optionLabel="label"
            style={{ width: '100%' }}
            onChange={e => setSelectedCompany(e.value)}
            placeholder="Vyberte si spoločnosť"
          />
        </div>
        {selectedCompany && (
          <DataTable
            stripedRows
            value={payments}
            size="small"
            lazy
            responsiveLayout="scroll"
            dataKey="id"
            emptyMessage="Žiadne dáta."
            header={header}
            paginator
            first={lazyParams.first}
            rows={20}
            totalRecords={totalRecords}
            onPage={onPage}
            onSort={onSort}
            rowExpansionTemplate={expandTemplate}
            expandedRows={expandedRows}
            onRowToggle={e => setExpandedRows(e.data)}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            filterDisplay="row"
            filters={filterValues}
            onFilter={e => setFilterValues(e.filters)}
            loading={loading}
            className="table-row-with-line"
          >
            <Column expander={allowExpansion} style={{ width: '5rem' }} />
            {/*<Column field={'provisionsExceedLimit'} header="" body={v => getPaymentExceedLimit(v)} />*/}
            <Column field={'messages'} header="" body={v => getMessagesIcons(v)} />
            <Column
              field={'processed'}
              filter={true}
              showFilterMenu={false}
              filterElement={processedFilterMenu}
              header="Spracovaná"
              sortable={true}
              body={v => isProcessedIcon(v.processed)}
            />
            <Column field={'id'} filter={true} showFilterMenu={false} header="Id" sortable={true} />
            <Column
              field={'isOutgoing'}
              header="Typ platby"
              filter={true}
              showFilterMenu={false}
              filterElement={isOutgoingFilterMenu}
              sortable={true}
              body={v => paymentType(v.isOutgoing)}
            />
            <Column field={'ref'} filter={true} showFilterMenu={false} header="Referencia" sortable={true} />
            <Column
              field={'date'}
              filter={true}
              showFilterMenu={false}
              filterElement={dateFilterTemplate}
              header="Dátum prijatia"
              sortable={true}
              body={v => formatFromLocalDate(v.date)}
            />
            <Column field={'amount'} header="Suma" sortable={true} body={v => v.amount + ' ' + v.currency} />
            <Column field={'accountName'} filter={true} showFilterMenu={false} header="Meno účtu" sortable={true} />
            <Column field={'variableSymbol'} filter={true} showFilterMenu={false} header="Var. symbol" sortable={true} />
            <Column header="Akcie" body={actionBodyTemplate}></Column>
          </DataTable>
        )}
      </div>
      <Dialog
        visible={deleteContactDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteProductDialogFooter}
        onHide={hideDeleteContactDialog}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          {entity && (
            <span>
              Naozaj chcete vymazať platbu:{' '}
              <b>{entity.ref + ' ' + '(' + entity.amount + ' ' + entity.currency + ') od ' + entity.accountName}</b>?
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default PaymentTable;
